import React, { useCallback, useRef, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FileEarmarkSpreadsheet, Funnel } from "react-bootstrap-icons";
import { downloadExcelFile } from "utils/downloadFiles";
import { baseUrl } from "features/api/fetchBaseQuery";
import CustomFilter from "./CustomFilter";
import "./styles.scss";
import { FilterFields } from "./types";
import { tableColumns } from "../types";

type Props = {
  columns: tableColumns[];
  submitFilter: (filters: FilterFields[]) => void;
  filterValues: FilterFields[];
  thereIsData: boolean;
  excelEndpoint: string;
  // Full column set used to localize the exported Excel headers. Defaults to
  // `columns`, but pass the unfiltered list when `columns` is a filter-only subset
  // (e.g. ProductsReport) so every exported column header is still translated.
  exportColumns?: tableColumns[];
};

// Base64-encode a (possibly non-ASCII, e.g. Hebrew) string as UTF-8 so it can ride
// in an HTTP header. The backend decodes with Convert.FromBase64String + UTF-8.
const toBase64Utf8 = (str: string): string => {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
};

const Header = ({
  columns,
  submitFilter,
  filterValues,
  thereIsData,
  excelEndpoint,
  exportColumns,
}: Props) => {
  const { t } = useTranslation();
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const collapsibleRef = useRef(null);
  const baseEndPointForExcelFile = `${baseUrl}/recapes/`;
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);

  const toggleContent = () => {
    setIsFilterVisible(!isFilterVisible);
  };

  const getFileName = (endpoint: string) => {
    switch (true) {
      case endpoint.includes("GetCarsSummaryAsExcelFile"):
        return "CarSummary.xlsx";
      case endpoint.includes("GetReportsByLicenseNumberAsExcelFile"):
        return "ReportsByLicenseNumber.xlsx";
      case endpoint.includes("GetVolunteersSummaryAsExcelFile"):
        return "VolunteersSummary.xlsx";
      case endpoint.includes("GetContollersSummaryAsExcelFile"):
        return "ContollersSummary.xlsx";
      case endpoint.includes("GetReportsByControllerAsExcelFile"):
        return "ReportsByController.xlsx";
      case endpoint.includes("GetProductsSummaryAsExcelFile"):
        return "ProductsSummary.xlsx";
      default:
        return "NoExcelName";
    }
  };

  const handleExcelDownload = () => {
    const fileName = getFileName(excelEndpoint);
    const filterObject = filterValues.reduce(
      (acc, cur) => ({
        ...acc,
        [cur.field]:
          // if value not exist in the cur it means it date type filter
          "value" in cur ? cur.value : `${cur.startDate}-${cur.endDate}`,
      }),
      {}
    );
    const body = JSON.stringify(filterObject);

    // Send the localized column headers (accessor -> translated label) so the
    // server-generated Excel matches the language selected in the web UI, instead
    // of the backend's hardcoded [DisplayName] Hebrew. Uses the full column set.
    const headerColumns = exportColumns ?? columns;
    const columnHeaders: Record<string, string> = Object.fromEntries(
      headerColumns
        .filter((col) => col.accessor && typeof col.Header === "string")
        .map((col) => [col.accessor, col.Header as string])
    );

    downloadExcelFile(
      `${baseEndPointForExcelFile}${excelEndpoint}`,
      body,
      fileName,
      setIsLoadingDownload,
      { "X-Column-Headers": toBase64Utf8(JSON.stringify(columnHeaders)) }
    );
  };
  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (
        collapsibleRef?.current &&
        !collapsibleRef?.current.contains(event.target as Node)
      ) {
        setIsFilterVisible(false);
      }
    },
    [setIsFilterVisible]
  );

  useEffect(() => {
    if (isFilterVisible) {
      document.addEventListener("click", handleOutsideClick);
    } else {
      document.removeEventListener("click", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [handleOutsideClick, isFilterVisible]);

  return (
    <div className="header-container">
      <div ref={collapsibleRef}>
        <button
          type="button"
          className="filter-button header-button"
          onClick={toggleContent}
          // disabled={!thereIsData}
        >
          <div className={`dot ${filterValues?.length > 0 ? "active" : ""}`} />
          {t("filter")}
          <Funnel height={15} width={15} />
        </button>
        {isFilterVisible && (
          <CustomFilter
            columns={columns}
            submitFilter={submitFilter}
            setIsFilterVisible={setIsFilterVisible}
            filterValues={filterValues}
          />
        )}
      </div>

      {isLoadingDownload ? (
        <Spinner animation="border" variant="secondary" />
      ) : (
        <button
          type="button"
          className="header-button"
          disabled={!thereIsData}
          onClick={handleExcelDownload}
        >
          {t("exportExcel")}
          {`  `}
          <FileEarmarkSpreadsheet height={20} width={20} />
        </button>
      )}
    </div>
  );
};

export default Header;
